<script>
import { authState } from '@app/store/authStore.svelte.js';
import { modalState, hideModal } from '@app/store/modalStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import { fade } from 'svelte/transition';
import { BarLoader, Circle } from 'svelte-loading-spinners';
import { onMount, tick } from 'svelte';
import Papa from 'papaparse';
import * as m from '@app/paraglide/messages.js';

/* Firebase */
import firebase from '@app/configs/firebase.js';
import 'firebase/compat/functions';

import { saveAs } from 'file-saver';

/* Chart.js */
import {
    Chart,
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend
} from 'chart.js';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const USAGE_FN = 'v8GetUsageReport';

const PAGE_SIZE = 50;

const teamId = authState.team ? authState.team.id : null;

function commonPayload(overrides = {}) {
	return {
		teamId,
		dateRange,
		customStart: dateRange === 'custom' ? startDate : undefined,
		customEnd: dateRange === 'custom' ? endDate : undefined,
		jobTypeFilter,
		compareEnabled,
		pageSize: PAGE_SIZE,
		...overrides,
	};
}

async function invokeUsage(payload) {
	const callable = firebase.functions().httpsCallable(USAGE_FN);
	const result = await callable(payload);
	return result.data;
}

/* ── State ── */
let jobs = $state.raw([]);
/** @type {null | Record<string, unknown>} */
let chartPayload = $state.raw(null);

let loading = $state(false);
let dateRange = $state('month');
let startDate = $state();
let endDate = $state();
let jobTypeFilter = $state('All');
let compareEnabled = $state(false);
let activeView = $state('table');
let chartMetric = $state('cost');

let totalJobs = $state(0);
let transcriptMins = $state(0);
let translationChars = $state(0);
let ttsChars = $state(0);
let embedJobs = $state(0);
let adJobs = $state(0);
let sceneDescriptionJobs = $state(0);
let totalCost = $state(0);

/** @type {null | Record<string, unknown>} */
let comparisonSummary = $state.raw(null);

/** @type {(string | null)[]} */
let tableBackwardStack = $state.raw([]);
let pageFetchCursor = $state(null);
let tableNextCursor = $state(null);

let chartCanvas = $state();
/** @type {Chart | null} */
let chartInstance = null;

let gettingJobs = $state(null);

function computeDateRange(rangeKey, customStart, customEnd) {
	let start;
	let end;
	switch (rangeKey) {
		case 'custom':
			start = new Date(customStart);
			end = new Date(customEnd);
			break;
		case 'week':
			start = new Date();
			end = new Date();
			start.setHours(start.getHours() - 168);
			break;
		case 'month':
			start = new Date();
			end = new Date();
			start.setMonth(start.getMonth() - 1);
			break;
		case 'year':
			start = new Date();
			end = new Date();
			start.setFullYear(start.getFullYear() - 1);
			break;
		default:
			start = new Date();
			end = new Date();
			start.setHours(start.getHours() - 24);
	}
	return { start, end };
}

function applySummary(s) {
	totalJobs = s.totalJobs ?? 0;
	transcriptMins = s.transcriptMins ?? 0;
	translationChars = s.translationChars ?? 0;
	ttsChars = s.ttsChars ?? 0;
	embedJobs = s.embedJobs ?? 0;
	adJobs = s.adJobs ?? 0;
	sceneDescriptionJobs = s.sceneDescriptionJobs ?? 0;
	totalCost = s.totalCost ?? 0;
}

function cleanupChartInstance() {
	if (chartInstance) {
		chartInstance.destroy();
		chartInstance = null;
	}
}

function getJobs() {
	loading = true;
	tableBackwardStack = [];
	pageFetchCursor = null;
	tableNextCursor = null;
	jobs = [];
	chartPayload = null;
	comparisonSummary = null;

	gettingJobs = (async () => {
		try {
			const range = computeDateRange(dateRange, startDate, endDate);
			if (Number.isNaN(range.start?.getTime()) || Number.isNaN(range.end?.getTime())) {
				toast.push(m.custom_date_warning(), { classes: ['toast-danger'] });
				return;
			}

			const data = await invokeUsage({
				mode: 'report',
				...commonPayload(),
			});

			applySummary(data.summary);
			comparisonSummary = data.comparisonSummary ?? null;
			chartPayload = data.chart;
			jobs = data.rows ?? [];
			tableNextCursor = data.nextCursor ?? null;
			pageFetchCursor = null;

			if (activeView === 'chart') {
				await tick();
				renderChartFromPayload();
			}
		} catch (error) {
			console.error('Error loading usage:', error);
			toast.push(error?.message || String(error), { classes: ['toast-danger'] });
		} finally {
			loading = false;
		}
	})();

	return gettingJobs;
}

async function loadTablePage(cursorDocId) {
	loading = true;
	try {
		const data = await invokeUsage({
			mode: 'page',
			...commonPayload(cursorDocId != null ? { cursor: cursorDocId } : {}),
		});
		jobs = data.rows ?? [];
		tableNextCursor = data.nextCursor ?? null;
		pageFetchCursor = cursorDocId;
	} catch (error) {
		console.error('Error loading usage page:', error);
		toast.push(error?.message || String(error), { classes: ['toast-danger'] });
	} finally {
		loading = false;
	}
}

async function tableGoNext() {
	if (!tableNextCursor) return;
	tableBackwardStack = [...tableBackwardStack, pageFetchCursor];
	await loadTablePage(tableNextCursor);
}

async function tableGoPrev() {
	if (tableBackwardStack.length === 0) return;
	const stack = [...tableBackwardStack];
	const prev = stack.pop();
	tableBackwardStack = stack;
	await loadTablePage(prev);
}

function renderChartFromPayload() {
	if (!chartCanvas || !chartPayload) return;

	cleanupChartInstance();

	const metricLabels = {
		cost: m.usage_metric_cost(),
		count: m.usage_metric_job_count(),
		minutes: m.usage_metric_minutes(),
		characters: m.usage_metric_characters(),
	};

	/** @type {string[]} */
	let labels;
	/** @type {Record<string, unknown>[]} */
	let datasets;

	if (!compareEnabled) {
		const single = chartPayload;
		labels = /** @type {string[]} */ (single.labels ?? []);
		const rows = /** @type {number[]} */ (single.datasetsByMetric?.[chartMetric] ?? []);
		datasets = [
			{
				label: m.usage_current_period(),
				data: rows,
				backgroundColor: 'rgba(30, 180, 178, 0.85)',
				borderColor: 'rgba(30, 180, 178, 1)',
				borderWidth: 1,
				borderRadius: 4,
				barPercentage: 0.6,
				categoryPercentage: 0.8,
			},
		];
	} else {
		labels = /** @type {string[]} */ (chartPayload.labels ?? []);
		const cur = /** @type {number[]} */ (chartPayload.currentByMetric?.[chartMetric] ?? []);
		const comp = /** @type {number[]} */ (chartPayload.comparisonByMetric?.[chartMetric] ?? []);
		datasets = [
			{
				label: m.usage_current_period(),
				data: cur,
				backgroundColor: 'rgba(30, 180, 178, 0.85)',
				borderColor: 'rgba(30, 180, 178, 1)',
				borderWidth: 1,
				borderRadius: 4,
				barPercentage: 0.45,
				categoryPercentage: 0.8,
			},
			{
				label: m.usage_previous_period(),
				data: comp,
				backgroundColor: 'rgba(30, 180, 178, 0.30)',
				borderColor: 'rgba(30, 180, 178, 0.5)',
				borderWidth: 1,
				borderRadius: 4,
				barPercentage: 0.45,
				categoryPercentage: 0.8,
			},
		];
	}

	chartInstance = new Chart(chartCanvas, {
		type: 'bar',
		data: { labels, datasets },
		options: {
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				legend: {
					display: compareEnabled,
					position: 'top',
					labels: { color: '#dee2e6' },
				},
				tooltip: {
					callbacks: {
						label: ctx => {
							const val = ctx.parsed.y;
							if (chartMetric === 'cost')
								return ` ${ctx.dataset.label}: $${val.toFixed(2)}`;
							if (chartMetric === 'minutes')
								return ` ${ctx.dataset.label}: ${val.toFixed(2)} ${m.mins_unit()}`;
							if (chartMetric === 'characters')
								return ` ${ctx.dataset.label}: ${val.toLocaleString()} ${m.chars_unit()}`;
							return ` ${ctx.dataset.label}: ${val}`;
						},
					},
				},
			},
			scales: {
				x: {
					grid: { color: 'rgba(108, 117, 125, 0.3)' },
					ticks: {
						color: '#dee2e6',
						maxRotation: compareEnabled ? 45 : 0,
						autoSkip: true,
						maxTicksLimit: compareEnabled ? 20 : 31,
					},
				},
				y: {
					beginAtZero: true,
					title: {
						display: true,
						text: metricLabels[chartMetric],
						color: '#dee2e6',
					},
					grid: { color: 'rgba(108, 117, 125, 0.3)' },
					ticks: {
						color: '#dee2e6',
						callback: val => {
							if (chartMetric === 'cost') return '$' + val.toFixed(2);
							return val;
						},
					},
				},
			},
		},
	});
}

async function exportCsv() {
	toast.push(m.exporting_results(), { classes: ['toast-info'] });

	const rowsOut = [];

	try {
		let csvCursor = null;
		while (true) {
			const chunk = await invokeUsage({
				...commonPayload(),
				mode: 'csvChunk',
				chunkSize: 800,
				...(csvCursor ? { cursor: csvCursor } : {}),
			});
			const batch = chunk.rows ?? [];
			for (const r of batch) {
				rowsOut.push({
					[m.job_id()]: r.id,
					[m.project_id()]: r.projectId,
					[m.project_name()]: r.projectName,
					[m.submitted_by()]: r.submittedBy,
					[m.created_on()]: r.createdOn ? new Date(r.createdOn) : '',
					[m.updated_on()]: r.updatedOn ? new Date(r.updatedOn) : '',
					[m.completed_on()]: r.completedOn ? new Date(r.completedOn) : '',
					[m.status()]: r.status,
					[m.status_message()]: r.statusMsg,
					[m.type()]: r.type,
					[m.duration()]: r.duration ? r.duration : 0,
					[m.characters()]: r.chars ? r.chars : 0,
					[m.estimated_cost()]: Number(r.estimatedCost ?? 0).toFixed(4),
				});
			}
			csvCursor = chunk.nextCursor ?? null;
			if (!csvCursor) break;
		}

		const csv = rowsOut.length ? Papa.unparse(rowsOut) : Papa.unparse([{}]);
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
		saveAs(blob, `ccc_usage_${new Date().toISOString().slice(0, 10)}.csv`, { autoBom: true });
	} catch (error) {
		console.error('CSV export failed:', error);
		toast.push(error?.message || String(error), { classes: ['toast-danger'] });
	}
}

onMount(() => {
	gettingJobs = getJobs();
});

$effect(() => {
	void chartMetric;
	void compareEnabled;
	void activeView;
	void chartPayload;

	if (activeView !== 'chart') {
		cleanupChartInstance();
		return () => cleanupChartInstance();
	}
	if (!chartPayload) {
		cleanupChartInstance();
		return () => cleanupChartInstance();
	}

	let cancelled = false;
	tick().then(() => {
		if (cancelled || !chartCanvas) return;
		renderChartFromPayload();
	});

	return () => {
		cancelled = true;
		cleanupChartInstance();
	};
});
</script>

<div transition:fade={{ duration: 100 }} class="modal {modalState.selected === 'usage' ? 'show d-block' : ''}" role="dialog" aria-labelledby="usageTitle" aria-modal="true" tabindex="-1" id="UsageModal">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <!-- Header -->
            <div class="modal-header border-bottom border-secondary">
                <h5 class="modal-title fw-semibold" id="usageTitle">
                    <i class="bi bi-bar-chart-line me-2" aria-hidden="true"></i>{m.reporting_usage()}
                </h5>
                <button type="button" class="btn-close" aria-label={m.close()} onclick={hideModal}></button>
            </div>

            <div class="modal-body px-4 py-3">
                <!-- ═══ Filter Bar ═══ -->
                <div class="d-flex flex-row align-items-center flex-wrap gap-2 mb-3">
                    <div class="d-flex align-items-center gap-1">
                        <label class="form-label mb-0 small fw-semibold text-secondary" for="dateRangeSelect">{m.date_range_select()}</label>
                        <select class="form-select form-select-sm" id="dateRangeSelect" bind:value={dateRange} style="width:auto;">
                            <option value="today">{m.date_range_24_hours()}</option>
                            <option value="week">{m.date_range_past_week()}</option>
                            <option value="month">{m.date_range_past_month()}</option>
                            <option value="year">{m.date_range_past_year()}</option>
                            <option value="custom">{m.date_range_custom()}</option>
                        </select>
                    </div>

                    {#if dateRange === 'custom'}
                    <div class="d-flex align-items-center gap-1">
                        <label class="form-label mb-0 small fw-semibold text-secondary" for="startDateInput">{m.custom_date_start()}</label>
                        <input class="form-control form-control-sm" id="startDateInput" type="date" bind:value={startDate} style="width:auto;" />
                    </div>
                    <div class="d-flex align-items-center gap-1">
                        <label class="form-label mb-0 small fw-semibold text-secondary" for="endDateInput">{m.custom_date_end()}</label>
                        <input class="form-control form-control-sm" id="endDateInput" type="date" bind:value={endDate} style="width:auto;" />
                    </div>
                    {/if}

                    <div class="d-flex align-items-center gap-1">
                        <label class="form-label mb-0 small fw-semibold text-secondary" for="jobTypeSelect">{m.job_type_filter()}</label>
                        <select class="form-select form-select-sm" id="jobTypeSelect" bind:value={jobTypeFilter} style="width:auto;">
                            <option value="All">{m.all()}</option>
                            <option value="transcription">{m.transcription()}</option>
                            <option value="translation">{m.translation()}</option>
                            <option value="text to speech">{m.text_to_speech()}</option>
                            <option value="embed">{m.closed_caption_embed()}</option>
                            <option value="automatic ad">{m.ad_job_type()}</option>
                            <option value="automatic shot descriptions">{m.scene_description_job_type()}</option>
                        </select>
                    </div>

                    <div class="ms-auto d-flex align-items-center gap-2">
                        {#if dateRange === 'custom' && (!startDate || !endDate)}
                        <span class="text-danger small">{m.custom_date_warning()}</span>
                        {/if}
                        <button type="button" class="btn btn-sm btn-outline-info" disabled={dateRange === 'custom' && (!startDate || !endDate)} onclick={() => { gettingJobs = getJobs(); }} aria-label={m.refresh_results()}>
                            <i class="bi bi-arrow-clockwise" aria-hidden="true"></i> {m.refresh()}
                        </button>
                        <button type="button" class="btn btn-sm btn-light" disabled={totalJobs === 0} onclick={exportCsv} aria-label={m.export_results()}>
                            <i class="bi bi-download" aria-hidden="true"></i> {m.export()}
                        </button>
                        {#if loading}
                        <Circle size="20" color="#1eb4b2" unit="px" duration="1s"></Circle>
                        {/if}
                    </div>
                </div>

                <!-- ═══ Summary Cards ═══ -->
                <div class="row g-2 mb-3">
                    <div class="col">
                        <div class="summary-card bg-dark text-light rounded-3 p-3 text-center">
                            <div class="summary-value">{totalJobs.toLocaleString()}</div>
                            <div class="summary-label">{m.usage_total_jobs()}</div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="summary-card bg-dark text-light rounded-3 p-3 text-center">
                            <div class="summary-value">{transcriptMins.toFixed(1)}</div>
                            <div class="summary-label">{m.transcription_minutes()}</div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="summary-card bg-dark text-light rounded-3 p-3 text-center">
                            <div class="summary-value">{translationChars.toLocaleString()}</div>
                            <div class="summary-label">{m.translation_characters()}</div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="summary-card bg-dark text-light rounded-3 p-3 text-center">
                            <div class="summary-value">{ttsChars.toLocaleString()}</div>
                            <div class="summary-label">{m.tts_characters()}</div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="summary-card bg-dark text-light rounded-3 p-3 text-center">
                            <div class="summary-value">{adJobs.toLocaleString()}</div>
                            <div class="summary-label">{m.ad_job_type()}</div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="summary-card bg-dark text-light rounded-3 p-3 text-center">
                            <div class="summary-value">{sceneDescriptionJobs.toLocaleString()}</div>
                            <div class="summary-label">{m.scene_description_job_type()}</div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="summary-card summary-card-accent bg-light text-dark rounded-3 p-3 text-center">
                            <div class="summary-value">${totalCost.toFixed(2)}</div>
                            <div class="summary-label">
                                {m.estimated_cost_short()}
                                <i class="bi bi-info-circle ms-1" title={m.estimated_cost_tooltip()} aria-hidden="true"></i>
                            </div>
                            <div class="summary-rate">{m.usage_pricing_note()}</div>
                        </div>
                    </div>
                </div>

                <!-- ═══ View Tabs ═══ -->
                <div class="d-flex align-items-center justify-content-between mb-2">
                    <ul class="nav nav-pills nav-sm" role="tablist">
                        <li class="nav-item" role="presentation">
                            <button class="nav-link {activeView === 'table' ? 'active' : ''}" type="button" role="tab" aria-selected={activeView === 'table'} onclick={() => { activeView = 'table'; }}>
                                <i class="bi bi-table me-1" aria-hidden="true"></i>{m.usage_view_table()}
                            </button>
                        </li>
                        <li class="nav-item" role="presentation">
                            <button class="nav-link {activeView === 'chart' ? 'active' : ''}" type="button" role="tab" aria-selected={activeView === 'chart'} onclick={() => { activeView = 'chart'; }}>
                                <i class="bi bi-bar-chart-fill me-1" aria-hidden="true"></i>{m.usage_view_chart()}
                            </button>
                        </li>
                    </ul>
                    {#if activeView === 'chart'}
                    <div class="d-flex align-items-center gap-3">
                        <div class="form-check form-switch mb-0">
                            <input class="form-check-input" type="checkbox" role="switch" id="compareSwitch" bind:checked={compareEnabled} onchange={() => { gettingJobs = getJobs(); }} />
                            <label class="form-check-label small" for="compareSwitch">{m.usage_compare_toggle()}</label>
                        </div>
                        <div class="d-flex align-items-center gap-1">
                            <label class="form-label mb-0 small fw-semibold text-secondary" for="chartMetricSelect">{m.usage_chart_metric()}</label>
                            <select class="form-select form-select-sm" id="chartMetricSelect" bind:value={chartMetric} style="width:auto;">
                                <option value="cost">{m.usage_metric_cost()}</option>
                                <option value="count">{m.usage_metric_job_count()}</option>
                                <option value="minutes">{m.usage_metric_minutes()}</option>
                                <option value="characters">{m.usage_metric_characters()}</option>
                            </select>
                        </div>
                    </div>
                    {/if}
                </div>

                <!-- ═══ Table View ═══ -->
                {#if activeView === 'table'}
                <div id="UsageTable" class="table-responsive rounded-3 border border-secondary">
                    <table class="table table-hover table-sm mb-0 align-middle">
                        <thead>
                            <tr>
                                <th class="small">{m.project_name()}</th>
                                <th class="small">{m.job_type()}</th>
                                <th class="small">{m.submitted_by()}</th>
                                <th class="small">{m.usage()}</th>
                                <th class="small">{m.status()}</th>
                                <th class="small">{m.status_message()}</th>
                                <th class="small">{m.estimated_cost()}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#await gettingJobs}
                            <tr>
                                <td colspan="7" class="text-center py-5">
                                    <BarLoader size="160" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
                                </td>
                            </tr>
                            {:then}
                            {#each jobs as job (job.id)}
                            <tr>
                                <td class="small text-truncate" style="max-width:160px;" title={job.projectName}>{job.projectName}</td>
                                <td class="small">{job.type}</td>
                                <td class="small text-truncate" style="max-width:120px;" title={job.submittedBy}>{job.submittedBy}</td>
                                {#if job.type === "embed"}
                                <td class="small">{m.file_count_one()}</td>
                                {:else}
                                <td class="small">{job.chars || ((job.duration || 0) / 60).toFixed(2)} {job.chars ? m.chars_unit() : m.mins_unit()}</td>
                                {/if}
                                <td class="small">
                                    <span class="badge {job.status === 'Passed' ? 'bg-success' : 'bg-danger'}">{job.status}</span>
                                </td>
                                <td class="small text-truncate" style="max-width:150px;" title={job.statusMsg}>{(job.statusMsg || '').substring(0, 30)}</td>
                                <td class="small fw-semibold">${Number(job.estimatedCost ?? 0).toFixed(4)}</td>
                            </tr>
                            {:else}
                            <tr>
                                <td colspan="7" class="text-center py-5">
                                    <p class="text-secondary m-0">{m.no_results_to_display()}</p>
                                </td>
                            </tr>
                            {/each}
                            {/await}
                        </tbody>
                    </table>
                </div>
                <div class="d-flex justify-content-between align-items-center mt-2 px-1 gap-2">
                    <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        disabled={tableBackwardStack.length === 0}
                        onclick={() => tableGoPrev()}
                        title={m.previous_slide()}
                        aria-label={m.previous_slide()}
                    >
                        <i class="bi bi-chevron-left" aria-hidden="true"></i>
                        <span class="d-none d-md-inline ms-1">{m.previous_slide()}</span>
                    </button>
                    <small class="text-secondary text-center flex-grow-1">
                        {#if totalJobs > 0}
                            {jobs.length.toLocaleString()} / {totalJobs.toLocaleString()} {m.usage_metric_job_count()}
                        {:else}
                            {jobs.length.toLocaleString()}
                        {/if}
                    </small>
                    <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        disabled={!tableNextCursor}
                        onclick={() => tableGoNext()}
                        title={m.next_slide()}
                        aria-label={m.next_slide()}
                    >
                        <span class="d-none d-md-inline me-1">{m.next_slide()}</span>
                        <i class="bi bi-chevron-right" aria-hidden="true"></i>
                    </button>
                </div>

                {:else if activeView === 'chart'}
                <div class="chart-container rounded-3 border border-secondary p-3">
                    {#await gettingJobs}
                    <div class="d-flex justify-content-center align-items-center" style="height:380px;">
                        <BarLoader size="160" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
                    </div>
                    {:then}
                    {#if totalJobs === 0 && !(compareEnabled && (comparisonSummary?.totalJobs ?? 0) > 0)}
                    <div class="d-flex justify-content-center align-items-center" style="height:380px;">
                        <p class="text-secondary">{m.usage_no_chart_data()}</p>
                    </div>
                    {:else}
                    <canvas bind:this={chartCanvas}></canvas>
                    {/if}
                    {/await}
                </div>
                {/if}
            </div>
        </div>
    </div>
</div>

<style lang="css">
    #UsageTable {
        max-height: 420px;
        overflow-y: auto;
    }

    .chart-container {
        position: relative;
        height: 420px;
    }

    .chart-container canvas {
        width: 100% !important;
        height: 380px !important;
    }

    .summary-value {
        font-size: 1.4rem;
        font-weight: 700;
        line-height: 1.2;
    }

    .summary-label {
        font-size: 0.72rem;
        margin-top: 2px;
        text-transform: uppercase;
        letter-spacing: 0.03em;
    }

    .summary-rate {
        font-size: 0.65rem;
        margin-top: 1px;
    }

    .nav-pills .nav-link {
        font-size: 0.85rem;
        padding: 0.35rem 0.75rem;
        color: #adb5bd;
    }

    .nav-pills .nav-link.active {
        background-color: rgba(30, 180, 178, 0.85);
        color: #fff;
    }

    .nav-pills .nav-link:not(.active):hover {
        color: #f8f9fa;
    }

    .table th {
        position: sticky;
        top: 0;
        z-index: 1;
    }
</style>
